/**
 * TanStack Query hooks for RevenueCat billing (AB#279).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { billingApi, type BillingTier } from '../api/billing';

export const billingKeys = {
  all: ['billing'] as const,
  status: () => [...billingKeys.all, 'status'] as const,
  checkout: (tier: BillingTier) => [...billingKeys.all, 'checkout', tier] as const,
};

/**
 * Current plan + billing config for the authenticated user.
 *
 * Pass a poll interval (ms) to watch for the webhook-driven plan flip after
 * returning from a checkout redirect (AB#450); `false`/omitted = no polling.
 */
export function useBillingStatus(refetchInterval: number | false = false) {
  return useQuery({
    queryKey: billingKeys.status(),
    queryFn: () => billingApi.status(),
    refetchInterval,
  });
}

/** Kick off checkout for a tier; resolves to the RevenueCat checkout URL. */
export function useCheckout() {
  return useMutation({
    mutationFn: (tier: BillingTier) => billingApi.checkout(tier),
  });
}

/**
 * Redeem a promo/comp code (AB#464). On success, refetch billing status so the
 * comped tier reflects; on failure the thrown ApiError carries a distinct
 * `error.code` (invalid|expired|exhausted|already_redeemed) for the caller.
 */
export function useRedeemCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => billingApi.redeem(code),
    onSuccess: () => qc.invalidateQueries({ queryKey: billingKeys.status() }),
  });
}

/**
 * Reconcile the stored plan from RC's active subscription set (AB#581). Called
 * once automatically in the mismatch state; refetches status on success so the
 * "Verifying…" badge clears when the plan catches up.
 */
export function useResyncBilling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => billingApi.resync(),
    onSuccess: () => qc.invalidateQueries({ queryKey: billingKeys.status() }),
  });
}

/**
 * Cancel the whole active web-billing subscription set at period end (AB#581).
 * Refetches status on success so rows flip to "Cancels {date}".
 */
export function useCancelSubscriptions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => billingApi.cancelSubscriptions(),
    onSuccess: () => qc.invalidateQueries({ queryKey: billingKeys.status() }),
  });
}

/**
 * Resolve a tier's checkout URL up front so the upgrade button can redirect
 * synchronously on click, with no loading-label swap (AB#449).
 */
export function useCheckoutUrl(tier: BillingTier, enabled = true) {
  return useQuery({
    queryKey: billingKeys.checkout(tier),
    queryFn: () => billingApi.checkout(tier),
    enabled,
  });
}
